import { useState } from 'react';
import { Download, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export function UserDataSettings() {
  const { user, signOut } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    if (!user) return;
    setExporting(true);

    try {
      const [
        { data: profile },
        { data: conversations },
        { data: agents },
        { data: prompts },
        { data: consentLogs },
        { data: loginSessions },
        { data: auditLogs },
      ] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.from('conversations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('agents').select('*').eq('owner_id', user.id).eq('scope_type', 'personal'),
        supabase.from('prompts').select('*').eq('owner_id', user.id).eq('scope_type', 'personal'),
        supabase.from('consent_logs' as any).select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('login_sessions').select('*').eq('user_id', user.id).order('logged_in_at', { ascending: false }),
        supabase.from('audit_logs').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      ]);

      // Fetch messages for each conversation
      const conversationIds = (conversations ?? []).map(c => c.id);
      let allMessages: any[] = [];
      if (conversationIds.length > 0) {
        const { data: messages } = await supabase
          .from('messages')
          .select('*')
          .in('conversation_id', conversationIds)
          .order('created_at', { ascending: true });
        allMessages = messages ?? [];
      }

      // Nest messages into conversations
      const messagesByConv = new Map<string, any[]>();
      for (const msg of allMessages) {
        const arr = messagesByConv.get(msg.conversation_id) || [];
        arr.push(msg);
        messagesByConv.set(msg.conversation_id, arr);
      }

      const conversasComMensagens = (conversations ?? []).map(conv => ({
        ...conv,
        mensagens: messagesByConv.get(conv.id) || [],
      }));

      const exportData = {
        exportado_em: new Date().toISOString(),
        perfil: profile,
        conversas: conversasComMensagens,
        agentes: agents ?? [],
        prompts: prompts ?? [],
        logs_consentimento: consentLogs ?? [],
        sessoes_login: loginSessions ?? [],
        logs_acesso: auditLogs ?? [],
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caldeira-ai-meus-dados-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Seus dados foram exportados com sucesso.');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Erro ao exportar dados. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!user || confirmText !== 'EXCLUIR') return;
    setDeleting(true);

    try {
      // Log deletion request in audit_logs
      await supabase.from('audit_logs').insert({
        user_id: user.id,
        action: 'account_deletion_requested',
        target_type: 'user',
        target_id: user.id,
        metadata: { requested_at: new Date().toISOString() },
      });

      // Log consent revocation
      await supabase.from('consent_logs' as any).insert({
        user_id: user.id,
        action: 'revoked',
        details: 'Usuário solicitou exclusão da conta e revogou consentimento de uso de dados.',
      });

      toast.success('Sua solicitação de exclusão foi registrada. Sua conta será removida em até 30 dias.');
      setDeleteOpen(false);
      setConfirmText('');

      // Sign out after a short delay
      setTimeout(() => signOut(), 2000);
    } catch (err) {
      console.error('Delete request error:', err);
      toast.error('Erro ao registrar solicitação. Tente novamente.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Export section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-primary">Exportar meus dados</h3>
          <p className="text-xs text-secondary mt-1">
            Faça o download de todos os seus dados pessoais armazenados na plataforma em formato JSON, incluindo perfil, conversas, agentes, prompts e logs.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting}
          className="gap-2"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? 'Exportando...' : 'Exportar dados'}
        </Button>
      </div>

      <div className="border-t border-color" />

      {/* Delete section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-destructive">Excluir minha conta</h3>
          <p className="text-xs text-secondary mt-1">
            Solicite a exclusão permanente da sua conta e de todos os seus dados pessoais. Esta ação não pode ser desfeita.
          </p>
        </div>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setDeleteOpen(true)}
          className="gap-2"
        >
          <Trash2 className="h-4 w-4" />
          Solicitar exclusão da conta
        </Button>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={(open) => { setDeleteOpen(open); if (!open) setConfirmText(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conta permanentemente</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                Tem certeza que deseja excluir sua conta? Todos os seus dados pessoais, conversas, agentes e prompts serão removidos permanentemente em até 30 dias. Esta ação não pode ser desfeita.
              </span>
              <span className="block text-sm font-medium text-primary">
                Digite <strong>EXCLUIR</strong> para confirmar:
              </span>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="EXCLUIR"
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-destructive"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRequest}
              disabled={confirmText !== 'EXCLUIR' || deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Processando...' : 'Confirmar exclusão'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
